import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, BellOff, X } from "lucide-react";
import { subscribeToPush, unsubscribeFromPush, isPushSubscribed, registerServiceWorker } from "@/utils/pushNotifications";
import { useUser } from "@/context/UserContext";

export default function PushNotificationBanner() {
  const { user } = useUser();
  const [status, setStatus] = useState("unknown"); // unknown | subscribed | not_subscribed | unsupported
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem("push_banner_dismissed") === "1");

  useEffect(() => {
    if (!user) return;
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
      setStatus("unsupported");
      return;
    }
    registerServiceWorker();
    isPushSubscribed().then((sub) => setStatus(sub ? "subscribed" : "not_subscribed"));
  }, [user]);

  const handleSubscribe = async () => {
    setLoading(true);
    const ok = await subscribeToPush(user.id);
    setStatus(ok ? "subscribed" : "not_subscribed");
    setLoading(false);
  };

  const handleUnsubscribe = async () => {
    setLoading(true);
    await unsubscribeFromPush(user.id);
    setStatus("not_subscribed");
    setLoading(false);
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem("push_banner_dismissed", "1");
  };

  if (!user || status === "unsupported" || status === "subscribed" || dismissed) return null;
  if (status === "unknown") return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -16 }}
        className="mx-4 mt-3 mb-0 rounded-2xl border border-[#14B8A6]/40 bg-[#FEF9E4] px-4 py-3 flex items-center justify-between gap-3 shadow-sm"
        data-testid="push-notification-banner"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[#14B8A6] rounded-xl flex items-center justify-center flex-shrink-0">
            <Bell size={18} className="text-gray-900" />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">Benachrichtigungen aktivieren</p>
            <p className="text-xs text-gray-500">Erhalte sofortige Benachrichtigungen bei neuen Anfragen und Nachrichten.</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={handleSubscribe}
            disabled={loading}
            data-testid="enable-push-btn"
            className="px-4 py-2 bg-[#14B8A6] text-gray-900 text-sm font-semibold rounded-xl hover:bg-[#0D9488] transition-colors disabled:opacity-50"
          >
            {loading ? "..." : "Aktivieren"}
          </button>
          <button
            onClick={handleDismiss}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-xl hover:bg-gray-100"
            data-testid="dismiss-push-banner"
          >
            <X size={16} />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * Small toggle for profile pages
 */
export function PushToggle() {
  const { user } = useUser();
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    isPushSubscribed().then(setSubscribed);
  }, [user]);

  const toggle = async () => {
    setLoading(true);
    if (subscribed) {
      await unsubscribeFromPush(user.id);
      setSubscribed(false);
    } else {
      const ok = await subscribeToPush(user.id);
      setSubscribed(ok);
    }
    setLoading(false);
  };

  if (!("PushManager" in window)) return null;

  return (
    <div className="flex items-center justify-between p-4 bg-white rounded-2xl border border-gray-200">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 bg-[#FEF3CE] rounded-xl flex items-center justify-center">
          {subscribed ? <Bell size={18} className="text-[#CA8A04]" /> : <BellOff size={18} className="text-gray-400" />}
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-900">Push-Benachrichtigungen</p>
          <p className="text-xs text-gray-500">{subscribed ? "Aktiviert" : "Deaktiviert"}</p>
        </div>
      </div>
      <button
        onClick={toggle}
        disabled={loading}
        data-testid="push-toggle-btn"
        className={`relative w-12 h-6 rounded-full transition-colors disabled:opacity-50 ${subscribed ? "bg-[#14B8A6]" : "bg-gray-300"}`}
      >
        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${subscribed ? "translate-x-6" : "translate-x-0.5"}`} />
      </button>
    </div>
  );
}
